import { ChatBubble } from "@/components/ai/chat/ChatBubble";
import { OnaTips } from "@/components/ai/chat/OnaTips";
import { Banner } from "@/components/flexkit/Banner";
import type { AgentMessageFragment, Message } from "@/hooks/use-stream-conversation";
import { Agents, type PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { filterMessagesByType, MESSAGE_TYPES } from "@/utils/chat";
import { getActiveEnvironmentId } from "@/utils/agent-executions";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { type FC, useEffect, useState } from "react";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useFlagTodoGroupingEnabled } from "@/hooks/use-flag";
import { GroupedMessages } from "@/components/ai/chat/GroupedMessages";

// OnaTips fade-out duration when conversation starts
// Kept short (300ms) to minimize gap before first message appears
const ONA_TIPS_FADE_OUT_MS = 300;

type ChatMessagesProps = {
    messages: Message[];
    onClick?: (block: AgentMessageFragment) => void;
    agentExecution: PlainAgentExecution;
    enableTips?: boolean;
} & PropsWithClassName;

export const ChatMessages: FC<ChatMessagesProps> = ({
    messages,
    onClick,
    agentExecution,
    className,
    enableTips = true,
}) => {
    const environmentId = getActiveEnvironmentId(agentExecution);
    const { data: environment } = useEnvironment(environmentId);
    const { value: isTodoGroupingEnabled } = useFlagTodoGroupingEnabled();

    // Check if we should show Ona tips
    // Show tips while environment is starting OR until agent starts responding (whichever is longer)
    const isEnvironmentStarting =
        environment?.status?.phase &&
        (environment.status.phase === EnvironmentPhase.STARTING ||
            environment.status.phase === EnvironmentPhase.CREATING ||
            environment.status.phase === EnvironmentPhase.UPDATING);

    const hasAgentResponded = messages.length >= 2; // User message + at least one agent message

    const shouldShowOnaTips =
        enableTips &&
        !hasAgentResponded && // Agent hasn't started responding yet
        agentExecution?.spec?.agentId === Agents.InEnvironmentOna.id && // In-environment agent
        agentExecution?.status?.phase !== AgentExecution_Phase.STOPPED && // Conversation is not stopped
        environment?.status?.phase && // Environment data exists
        isEnvironmentStarting;

    // State for managing fade-out animation
    const [isVisible, setIsVisible] = useState(shouldShowOnaTips);
    const [shouldRender, setShouldRender] = useState(shouldShowOnaTips);

    useEffect(() => {
        if (shouldShowOnaTips) {
            setShouldRender(true);
            setIsVisible(true);
        } else if (shouldRender) {
            // Start fade out
            setIsVisible(false);
            // Remove from DOM after animation completes
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, ONA_TIPS_FADE_OUT_MS);
            return () => clearTimeout(timer);
        }
    }, [shouldShowOnaTips, shouldRender]);

    return (
        <div
            className={cn(
                "mx-auto flex h-full w-full max-w-[700px] flex-col items-start p-4 sm:p-6",
                // When todo grouping is disabled, use gap-4 for consistent spacing (original behavior)
                !isTodoGroupingEnabled && "gap-4",
                className,
            )}
        >
            {isTodoGroupingEnabled ? (
                <GroupedMessages
                    messages={messages}
                    agentExecution={agentExecution}
                    onClick={onClick}
                    data-tracking-id="default-tracking-id"
                />
            ) : (
                messages.map((message, index) => (
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={message}
                        key={message.id}
                        onClick={onClick}
                        isLast={index === messages.length - 1}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none // the component delegates the onClick handler to its children
                    />
                ))
            )}

            {shouldRender && (
                <div
                    className={cn(
                        "w-full transition-opacity duration-300 ease-out",
                        isVisible ? "opacity-100" : "opacity-0",
                        { "mt-4": isTodoGroupingEnabled },
                    )}
                >
                    <OnaTips environment={environment} />
                </div>
            )}
            {agentExecution?.status?.warningMessage && (
                <Banner
                    variant="warning"
                    text={agentExecution.status.warningMessage}
                    data-testid="agent-execution-warning-banner"
                />
            )}
            {agentExecution?.status?.failureMessage && (
                <Banner
                    variant="danger"
                    text={agentExecution.status.failureMessage}
                    data-testid="agent-execution-failure-banner"
                />
            )}
        </div>
    );
};
